/**
 * Sidebar — the desktop-only left nav column of the AppShell.
 * Renders brand chip, workspace badge, grouped navigation, and the
 * profile bottom card. Nav groups are passed in; role/permission
 * filtering happens in AppShell so this stays presentational.
 */
import { NavLink } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Buildings, SignOut, Briefcase } from "@phosphor-icons/react";
import { initials } from "@/lib/helpers";
import { cn } from "@/lib/utils";
import { FileImage } from "@/components/ui/file-image";

export default function Sidebar({
  brand, brandInitial, org, user, navGroups, onOpenProfile, onLogout,
}) {
  return (
    <aside className="hidden lg:flex w-64 border-r border-border bg-card flex-col sticky top-0 h-screen">
      {/* --- Brand + Workspace badge --- */}
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center overflow-hidden">
            {(brand.effective_logo_url || brand.logo_url) ? (
              <img src={brand.effective_logo_url || brand.logo_url} alt={brand.brand_name} className="h-9 w-9 object-contain" />
            ) : (
              <span className="text-primary-foreground font-display font-bold text-lg">{brandInitial}</span>
            )}
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-tight tracking-tight">{brand.brand_name}</div>
            <div className="overline text-[10px] mt-0.5">ERP · v1.0</div>
          </div>
        </div>
        {org?.name && (
          <div
            className="mt-3 flex items-center gap-1.5 rounded-md bg-primary/8 border border-primary/25 px-2.5 py-1.5 text-primary"
            title={`Active workspace: ${org.name}`}
            data-testid="workspace-badge-sidebar"
          >
            <Buildings size={13} weight="fill" className="shrink-0" />
            <div className="min-w-0">
              <div className="text-[9.5px] uppercase tracking-widest text-primary/70 leading-none mb-0.5">Workspace</div>
              <div className="text-[12px] font-semibold leading-tight truncate">{org.name}</div>
            </div>
          </div>
        )}
      </div>

      {/* --- Grouped nav --- */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {navGroups.map((group, gi) => (
          <div key={group.label} className={cn(gi > 0 && "mt-4")}>
            <div className={cn(
              "overline mb-2 flex items-center gap-1.5",
              group.isEnterprise ? "px-2 text-primary" : "px-3"
            )}>
              {group.isEnterprise && <Briefcase size={11} weight="duotone" />}
              {group.isEnterprise
                ? <span className="normal-case text-[9.5px] leading-tight" title={group.label}>Enterprise Workforce Suite</span>
                : group.label}
            </div>
            <ul className="space-y-0.5">
              {group.items.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <NavLink
                    to={to}
                    data-testid={`nav-${to.replace('/', '').replace(/\//g, '-')}`}
                    className={({ isActive }) => cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                      "hover:bg-muted hover:text-foreground",
                      isActive ? "bg-primary/10 text-primary font-semibold" : "text-muted-foreground"
                    )}
                  >
                    <Icon size={18} weight="duotone" />
                    <span>{label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      {/* --- Profile card (bottom) --- */}
      <div className="p-3 border-t border-border">
        <div
          role="button"
          tabIndex={0}
          onClick={onOpenProfile}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenProfile(); } }}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted transition-colors text-left cursor-pointer"
          data-testid="profile-open-btn"
        >
          <Avatar className="h-9 w-9">
            {user?.photo_file_id ? (
              <FileImage
                fileId={user.photo_file_id}
                alt=""
                className="aspect-square h-full w-full object-cover"
                fallback={
                  <AvatarFallback className="bg-primary/15 text-primary font-semibold text-sm">
                    {initials(user?.name || user?.email)}
                  </AvatarFallback>
                }
              />
            ) : (
              <AvatarFallback className="bg-primary/15 text-primary font-semibold text-sm">
                {initials(user?.name || user?.email)}
              </AvatarFallback>
            )}
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button
            variant="ghost" size="icon"
            onClick={(e) => { e.stopPropagation(); onLogout(); }}
            data-testid="logout-btn"
            title="Sign out"
          >
            <SignOut size={18} />
          </Button>
        </div>
      </div>
    </aside>
  );
}
